/**
 * Process Definitions Page - Manage authority's process definitions
 *
 * Allows authorities to:
 * - View their own process definitions
 * - View available system templates
 * - Copy templates to create their own process definitions
 * - Create new process definitions
 * - Edit existing process definitions
 * - Delete process definitions
 */

import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Description as ProcessIcon,
  Visibility as ViewIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { bpmnApi } from '../features/bpmn';
import { useCryptoKeyStore } from '../features/encryption';
import { CitizenLinkDialog } from '../components/CitizenLinkDialog';
import type { ProcessDefinitionWithOwnership, CopyTemplateRequest, CreateProcessDefinitionRequest } from '../features/bpmn/types/bpmn.types';

const DEFAULT_BPMN_XML = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
                  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
                  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
                  xmlns:di="http://www.omg.org/spec/DD/20100524/DI"
                  id="Definitions_1"
                  targetNamespace="http://bpmn.io/schema/bpmn">
  <bpmn:process id="PROCESS_KEY" name="PROCESS_NAME" isExecutable="true">
    <bpmn:startEvent id="StartEvent_1" name="Start" />
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="PROCESS_KEY">
      <bpmndi:BPMNShape id="_BPMNShape_StartEvent_1" bpmnElement="StartEvent_1">
        <dc:Bounds x="179" y="159" width="36" height="36" />
        <bpmndi:BPMNLabel>
          <dc:Bounds x="185" y="202" width="24" height="14" />
        </bpmndi:BPMNLabel>
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export function ProcessDefinitionsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { activeKey, gkdkSlots, loadGKDKSlots, isLoading: cryptoLoading } = useCryptoKeyStore();
  const [gkdkLoadAttempted, setGkdkLoadAttempted] = useState(false);

  // Load GKDK slots on mount to get authority IDs
  useEffect(() => {
    if (activeKey && gkdkSlots.length === 0 && !gkdkLoadAttempted) {
      setGkdkLoadAttempted(true);
      loadGKDKSlots();
    }
  }, [activeKey, gkdkSlots.length, loadGKDKSlots, gkdkLoadAttempted]);

  // Get the first authority ID from GKDK slots (employee's authority)
  const authorityId = gkdkSlots.length > 0 ? gkdkSlots[0].authorityId : null;

  const [tabValue, setTabValue] = useState(0);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ProcessDefinitionWithOwnership | null>(null);
  const [selectedProcess, setSelectedProcess] = useState<ProcessDefinitionWithOwnership | null>(null);
  const [selectedProcessForLink, setSelectedProcessForLink] = useState<ProcessDefinitionWithOwnership | null>(null);
  const [copyForm, setCopyForm] = useState({ newKey: '', newName: '' });
  const [createForm, setCreateForm] = useState({ key: '', name: '' });

  // Fetch own process definitions
  const {
    data: myDefinitions,
    isLoading: myLoading,
    error: myError,
  } = useQuery({
    queryKey: ['my-definitions', authorityId],
    queryFn: () => bpmnApi.getMyDefinitions(authorityId!),
    enabled: !!authorityId,
  });

  // Fetch templates
  const {
    data: templates,
    isLoading: templatesLoading,
    error: templatesError,
  } = useQuery({
    queryKey: ['templates'],
    queryFn: bpmnApi.getTemplates,
  });

  // Copy template mutation
  const copyMutation = useMutation({
    mutationFn: ({ templateKey, request }: { templateKey: string; request: CopyTemplateRequest }) =>
      bpmnApi.copyTemplate(templateKey, authorityId!, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-definitions'] });
      handleCloseCopyDialog();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (key: string) => bpmnApi.deleteMyDefinition(authorityId!, key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-definitions'] });
      handleCloseDeleteDialog();
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (request: CreateProcessDefinitionRequest) =>
      bpmnApi.createMyDefinition(authorityId!, request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['my-definitions'] });
      handleCloseCreateDialog();
      // Navigate to edit the newly created process
      navigate(`/admin/bpmn-editor?key=${data.key}&own=true`);
    },
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenCopyDialog = useCallback((template: ProcessDefinitionWithOwnership) => {
    setSelectedTemplate(template);
    setCopyForm({
      newKey: `${template.key}-${authorityId?.substring(0, 8) || 'copy'}`,
      newName: `${template.name || template.key} (Kopie)`,
    });
    setCopyDialogOpen(true);
  }, [authorityId]);

  const handleCloseCopyDialog = useCallback(() => {
    setCopyDialogOpen(false);
    setSelectedTemplate(null);
    setCopyForm({ newKey: '', newName: '' });
  }, []);

  const handleCopySubmit = useCallback(() => {
    if (!selectedTemplate || !copyForm.newKey) return;
    copyMutation.mutate({
      templateKey: selectedTemplate.key,
      request: {
        newKey: copyForm.newKey,
        newName: copyForm.newName || undefined,
      },
    });
  }, [selectedTemplate, copyForm, copyMutation]);

  const handleOpenDeleteDialog = useCallback((process: ProcessDefinitionWithOwnership) => {
    setSelectedProcess(process);
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedProcess(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!selectedProcess) return;
    deleteMutation.mutate(selectedProcess.key);
  }, [selectedProcess, deleteMutation]);

  const handleEditProcess = useCallback((key: string) => {
    navigate(`/admin/bpmn-editor?key=${key}&own=true`);
  }, [navigate]);

  const handleOpenCreateDialog = useCallback(() => {
    setCreateForm({ key: '', name: '' });
    setCreateDialogOpen(true);
  }, []);

  const handleCloseCreateDialog = useCallback(() => {
    setCreateDialogOpen(false);
    setCreateForm({ key: '', name: '' });
  }, []);

  const handleCreateSubmit = useCallback(() => {
    if (!createForm.key || !createForm.name) return;
    const xml = DEFAULT_BPMN_XML
      .replace(/PROCESS_KEY/g, createForm.key)
      .replace(/PROCESS_NAME/g, createForm.name);
    createMutation.mutate({
      name: createForm.name,
      xml,
    });
  }, [createForm, createMutation]);

  const handleOpenLinkDialog = useCallback((process: ProcessDefinitionWithOwnership) => {
    setSelectedProcessForLink(process);
    setLinkDialogOpen(true);
  }, []);

  const handleCloseLinkDialog = useCallback(() => {
    setLinkDialogOpen(false);
    setSelectedProcessForLink(null);
  }, []);

  const renderProcessCard = (process: ProcessDefinitionWithOwnership, isTemplate: boolean = false) => (
    <Grid item xs={12} sm={6} md={4} key={process.key}>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <ProcessIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" component="h3" noWrap>
              {process.name || process.key}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Key: {process.key}
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
            <Chip label={`v${process.version}`} size="small" variant="outlined" />
            {isTemplate && <Chip label="Vorlage" size="small" color="info" />}
            {process.copiedFromKey && (
              <Chip label="Kopie" size="small" color="secondary" />
            )}
          </Box>

          {process.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {process.description}
            </Typography>
          )}
        </CardContent>

        <CardActions sx={{ justifyContent: 'flex-end' }}>
          {isTemplate ? (
            <>
              <Tooltip title="Vorlage ansehen">
                <IconButton size="small">
                  <ViewIcon />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                startIcon={<CopyIcon />}
                onClick={() => handleOpenCopyDialog(process)}
              >
                Kopieren
              </Button>
            </>
          ) : (
            <>
              <Tooltip title="Link teilen">
                <IconButton size="small" color="primary" onClick={() => handleOpenLinkDialog(process)}>
                  <ShareIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Bearbeiten">
                <IconButton size="small" onClick={() => handleEditProcess(process.key)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Löschen">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleOpenDeleteDialog(process)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </CardActions>
      </Card>
    </Grid>
  );

  if (!activeKey) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Bitte melden Sie sich an, um Ihre Prozessdefinitionen zu verwalten.
        </Alert>
      </Box>
    );
  }

  // Show loading if we're still loading GKDK slots from IndexedDB
  if (cryptoLoading || (!authorityId && !gkdkLoadAttempted)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If we've attempted to load GKDK slots but still have no authority
  if (!authorityId && gkdkLoadAttempted) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Sie müssen zuerst eine Behörde einrichten, bevor Sie Prozessdefinitionen erstellen können.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Prozessdefinitionen
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Neuer Prozess
        </Button>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Verwalten Sie Ihre eigenen Prozessdefinitionen oder kopieren Sie Vorlagen, um sie anzupassen.
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`Meine Prozesse (${myDefinitions?.length || 0})`} />
          <Tab label={`Vorlagen (${templates?.length || 0})`} />
        </Tabs>
      </Box>

      {/* My Processes Tab */}
      <TabPanel value={tabValue} index={0}>
        {myLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : myError ? (
          <Alert severity="error">
            Fehler beim Laden Ihrer Prozessdefinitionen
          </Alert>
        ) : myDefinitions?.length === 0 ? (
          <Alert severity="info">
            Sie haben noch keine eigenen Prozessdefinitionen.
            Erstellen Sie einen neuen Prozess oder kopieren Sie eine Vorlage.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {myDefinitions?.map((process) => renderProcessCard(process))}
          </Grid>
        )}
      </TabPanel>

      {/* Templates Tab */}
      <TabPanel value={tabValue} index={1}>
        {templatesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : templatesError ? (
          <Alert severity="error">
            Fehler beim Laden der Vorlagen
          </Alert>
        ) : templates?.length === 0 ? (
          <Alert severity="info">
            Es sind keine Vorlagen verfügbar.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {templates?.map((template) => renderProcessCard(template, true))}
          </Grid>
        )}
      </TabPanel>

      {/* Copy Dialog */}
      <Dialog open={copyDialogOpen} onClose={handleCloseCopyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Vorlage kopieren</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Erstellen Sie eine Kopie der Vorlage "{selectedTemplate?.name || selectedTemplate?.key}",
            die Sie nach Belieben anpassen können.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Neuer Schlüssel"
            value={copyForm.newKey}
            onChange={(e) => setCopyForm((prev) => ({ ...prev, newKey: e.target.value }))}
            sx={{ mb: 2 }}
            helperText="Eindeutiger technischer Schlüssel für den neuen Prozess"
          />
          <TextField
            fullWidth
            label="Neuer Name"
            value={copyForm.newName}
            onChange={(e) => setCopyForm((prev) => ({ ...prev, newName: e.target.value }))}
            helperText="Anzeigename für den neuen Prozess"
          />
          {copyMutation.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Fehler beim Kopieren der Vorlage
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCopyDialog}>Abbrechen</Button>
          <Button
            onClick={handleCopySubmit}
            variant="contained"
            disabled={!copyForm.newKey || copyMutation.isPending}
          >
            {copyMutation.isPending ? 'Wird kopiert...' : 'Kopieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Prozessdefinition löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie die Prozessdefinition "{selectedProcess?.name || selectedProcess?.key}" wirklich löschen?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Alert>
          {deleteMutation.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Fehler beim Löschen der Prozessdefinition
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Abbrechen</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Wird gelöscht...' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Neuen Prozess erstellen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Erstellen Sie eine neue Prozessdefinition für Ihre Behörde.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Prozess-Schlüssel"
            value={createForm.key}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
            sx={{ mb: 2 }}
            helperText="Eindeutiger technischer Schlüssel (z.B. mein-antrag)"
          />
          <TextField
            fullWidth
            label="Prozess-Name"
            value={createForm.name}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
            helperText="Anzeigename für den Prozess"
          />
          {createMutation.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Fehler beim Erstellen des Prozesses
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Abbrechen</Button>
          <Button
            onClick={handleCreateSubmit}
            variant="contained"
            disabled={!createForm.key || !createForm.name || createMutation.isPending}
          >
            {createMutation.isPending ? 'Wird erstellt...' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Citizen Link Dialog */}
      {selectedProcessForLink && authorityId && (
        <CitizenLinkDialog
          open={linkDialogOpen}
          onClose={handleCloseLinkDialog}
          processKey={selectedProcessForLink.key}
          processName={selectedProcessForLink.name}
          authorityKeyId={authorityId}
        />
      )}
    </Box>
  );
}
